/**
 * Máquina de estados conversacional e envelope de eventos.
 *
 * Este arquivo é o contrato compartilhado entre o front e o Presence Runtime.
 * Existe por um motivo concreto: na auditoria do pacote, o normalizador do
 * starter emitia um envelope em camelCase com seis campos, enquanto o
 * u1-event-catalog.json exigia snake_case com event_id e tenant_id. Os dois
 * lados divergiram porque nada os obrigava a concordar. Agora ambos importam
 * daqui, e a divergência vira erro de compilação.
 */

// ---------------------------------------------------------------------------
// Estados conversacionais (o que o runtime está fazendo)
// ---------------------------------------------------------------------------

export const CONVERSATION_STATES = [
  'IDLE', 'CONNECTING', 'LISTENING', 'USER_SPEAKING', 'THINKING', 'PREAMBLE',
  'TOOL_RUNNING', 'WAITING_APPROVAL', 'ASSISTANT_SPEAKING', 'INTERRUPTED',
  'RECONNECTING', 'PRIVATE', 'ERROR',
] as const;

export type ConversationState = (typeof CONVERSATION_STATES)[number];

/** Transições permitidas. Qualquer outra é bug, não surpresa. */
export const TRANSITIONS: Record<ConversationState, readonly ConversationState[]> = {
  IDLE:               ['CONNECTING'],
  CONNECTING:         ['LISTENING', 'ERROR'],
  // LISTENING -> THINKING existe porque nem todo turno passa por fala. Um turno
  // digitado, ou uma transcrição que chega pronta, é um turno legítimo e não
  // deve ser forçado a fingir USER_SPEAKING para caber na rota. Fingir custou
  // caro: o servidor percorria LISTENING -> USER_SPEAKING -> THINKING emitindo
  // um evento por passo, o cliente recusava o passo do meio, e cada turno
  // terminava em sincronização forçada com o console cheio de aviso.
  LISTENING:          ['USER_SPEAKING', 'THINKING', 'RECONNECTING', 'PRIVATE', 'ERROR'],
  USER_SPEAKING:      ['LISTENING', 'THINKING', 'RECONNECTING', 'ERROR'],
  // THINKING -> LISTENING existe porque nem todo turno produz fala. O cérebro
  // pode ficar indisponível, devolver vazio, ou o usuário desistir — e voltar a
  // escutar é o desfecho honesto disso. Sem esta aresta, a única saída de
  // THINKING era ERROR, e a recuperação de uma falha de cérebro FALHAVA: o
  // servidor tentava voltar a LISTENING, tomava recusa do contrato, e o usuário
  // via "transição inválida" no lugar de "o cérebro não respondeu". Um defeito
  // que se disfarçava de outro. É a irmã de LISTENING -> THINKING, acima, e
  // pela mesma razão: fingir estado para caber na rota sai caro.
  THINKING:           ['ASSISTANT_SPEAKING', 'PREAMBLE', 'LISTENING', 'ERROR'],
  PREAMBLE:           ['TOOL_RUNNING', 'ASSISTANT_SPEAKING', 'ERROR'],
  TOOL_RUNNING:       ['ASSISTANT_SPEAKING', 'WAITING_APPROVAL', 'RECONNECTING', 'ERROR'],
  WAITING_APPROVAL:   ['TOOL_RUNNING', 'LISTENING', 'ERROR'],
  ASSISTANT_SPEAKING: ['INTERRUPTED', 'LISTENING', 'RECONNECTING', 'ERROR'],
  INTERRUPTED:        ['USER_SPEAKING', 'LISTENING'],
  RECONNECTING:       ['LISTENING', 'ERROR'],
  PRIVATE:            ['LISTENING'],
  ERROR:              ['IDLE'],
};

export const canTransition = (from: ConversationState, to: ConversationState): boolean =>
  TRANSITIONS[from].includes(to);

// ---------------------------------------------------------------------------
// Modo de voz — decisão de produto, alternável pelo usuário no painel
// ---------------------------------------------------------------------------

/**
 * `hybrid` é o padrão: o Realtime escuta (VAD, detecção de turno, barge-in,
 * transcrição) e o OmniVoice fala com a voz do Diego. Custa latência maior no
 * primeiro áudio e preserva a identidade da voz.
 *
 * `realtime` é speech-to-speech puro: menor latência, voz da OpenAI, o Diego
 * sai de cena.
 *
 * `text` desliga o áudio de saída — acessibilidade e ambientes silenciosos.
 */
export const VOICE_MODES = ['hybrid', 'realtime', 'text'] as const;
export type VoiceMode = (typeof VOICE_MODES)[number];

export const DEFAULT_VOICE_MODE: VoiceMode = 'hybrid';

// ---------------------------------------------------------------------------
// Envelope de eventos — snake_case, alinhado ao u1-event-catalog.json
// ---------------------------------------------------------------------------

export interface U1Envelope {
  /** identificador único do evento; base da deduplicação no replay */
  event_id: string;
  /** monotônico por sessão; o cliente reconhece o último e pede o resto */
  seq: number;
  type: U1EventType;
  occurred_at: string;
  tenant_id: string;
  presence_session_id: string;
  trace_id?: string;
}

export type U1Event<P = unknown> = U1Envelope & { payload: P };

export const U1_EVENTS = [
  'u1.session.ready', 'u1.session.reconnecting', 'u1.session.recovered', 'u1.session.ended',
  'u1.user.speech_started', 'u1.user.speech_stopped',
  'u1.user.transcript_delta', 'u1.user.transcript_final',
  'u1.will.thinking', 'u1.will.speech_started', 'u1.will.transcript_delta',
  'u1.will.interrupted', 'u1.will.speech_ended',
  'u1.tool.started', 'u1.tool.completed', 'u1.tool.partial', 'u1.tool.failed',
  'u1.approval.required', 'u1.approval.granted', 'u1.approval.rejected', 'u1.approval.expired',
  'u1.deep_work.started', 'u1.deep_work.progress', 'u1.deep_work.completed', 'u1.deep_work.cancelled',
  // Renomeado de `u1.mission.updated` (achado D-01 do SPEC_REVIEW-DISTANCIA-V0).
  // Por que mudou: o nome dizia "missão" e o payload sempre foi
  // `{voice_mode, applies_from}` — conteúdo de voz viajando num nome do domínio
  // de missão. As tabelas `missions`/`mission_actions` foram removidas na
  // migração 0010 (17/08), quando a tarefa virou a unidade e o passo passou a
  // morar dentro dela: o domínio que o nome citava deixou de existir, e o nome
  // sobreviveu à coisa que ele nomeava. Não foi capricho — era um nome que
  // mentia sobre o próprio conteúdo, e quem lesse o fio acreditaria nele.
  //
  // Por que este nome: o modo de voz é atributo da sessão (coluna
  // `sessions.voice_mode`, `session.voiceMode`), então o domínio é `session`,
  // como em `u1.session.ready`; e o sufixo é o fato consumado, no particípio
  // que o catálogo já usa (`u1.state.changed`, `u1.memory.persisted`). É o par
  // servidor do pedido do cliente `u1.client.voice_mode`: o cliente pede, o
  // servidor conta que mudou.
  //
  // Isto NÃO é alinhamento com a SPEC-EVENT-001. A interseção entre o catálogo
  // canônico das SPECs e este é zero, e renomear tudo quebraria cliente,
  // servidor e persistência ao mesmo tempo — decisão de produto pendente.
  'u1.session.voice_mode_changed', 'u1.memory.persisted',
  'u1.privacy.enabled', 'u1.privacy.disabled',
  'u1.state.changed', 'u1.error',
] as const;

export type U1EventType = (typeof U1_EVENTS)[number];

export const isU1Event = (v: unknown): v is U1EventType =>
  typeof v === 'string' && (U1_EVENTS as readonly string[]).includes(v);

/** Todo evento carrega tenant e sessão. O invariante do blueprint vira tipo. */
export function assertEnvelope(e: Partial<U1Envelope>): asserts e is U1Envelope {
  for (const f of ['event_id', 'seq', 'type', 'occurred_at', 'tenant_id', 'presence_session_id'] as const) {
    if (e[f] === undefined || e[f] === null || e[f] === '') {
      throw new Error(`envelope U1 inválido: campo obrigatório ausente — ${f}`);
    }
  }
}
